import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Popover } from 'antd'
import { nanoid } from 'nanoid'
import shallow from 'zustand/shallow'

import PerButton from '../Button'
import PerButtonCircle from '../ButtonCircle'
import PerFlexRow from '../FlexRow'
import PerText from '../Text'
import { useForecastModalStore, ForecastModalSaveButton, ForecastModalNewHiring } from '.'
import { Country } from '../../../types'
import { ForeacastModalTabs } from './types'

interface IForecastModalFooterProps {
  companyId: string
  country: Country
}

export const ForecastModalFooter: FC<IForecastModalFooterProps> = ({ companyId, country }) => {
  const { t } = useTranslation()

  const [popoverVisible, togglePopover] = useState<boolean>(false)

  const [
    activeTab,
    toggleModal,
    deleteEmployeesForecast,
    employeesSelectedRows,
    hiringsTableForm,
    hiringsSelectedRows,
    setHiringsSelectedRows,
    setMultipleHiringsForDeletion,
    employeeForecastHasChanges,
  ] = useForecastModalStore(
    (state) => [
      state.activeTab,
      state.toggleModal,
      state.deleteEmployeesForecast,
      state.employeesSelectedRows,
      state.hiringsTableForm,
      state.hiringsSelectedRows,
      state.setHiringsSelectedRows,
      state.setMultipleHiringsForDeletion,
      state.employeeForecastHasChanges,
    ],
    shallow,
  )

  const handleDeleteEmployeeForecast = () => {
    deleteEmployeesForecast()
  }

  const handleDeleteHiringsForecast = () => {
    const selectedRows = hiringsSelectedRows.map((row: ForecastModalNewHiring) => row.id)

    const hirings: ForecastModalNewHiring[] = hiringsTableForm
      .getValues()
      .hirings.filter((hiring) => !selectedRows.includes(hiring.id))
      .map((hiring) => {
        return { ...hiring, key: nanoid() }
      })

    setHiringsSelectedRows([])
    setMultipleHiringsForDeletion(selectedRows)

    hiringsTableForm.reset({ hirings })
  }

  useEffect(() => {
    if (activeTab === ForeacastModalTabs.Employees && !!popoverVisible && employeesSelectedRows.length === 0) {
      togglePopover(false)
    }
  }, [activeTab, popoverVisible, employeesSelectedRows])

  useEffect(() => {
    if (activeTab === ForeacastModalTabs.Hirings && !!popoverVisible && hiringsSelectedRows.length === 0) {
      togglePopover(false)
    }
  }, [activeTab, popoverVisible, hiringsSelectedRows])

  return (
    <PerFlexRow align="middle" justify="space-between">
      <PerFlexRow>
        <PerButton
          text={t('global.button.cancel')}
          color="secondary"
          before="close_stroke"
          variant="pastel"
          type="button"
          onClick={() => toggleModal(false)}
        />
        {activeTab === ForeacastModalTabs.Employees && (
          <Popover
            trigger="click"
            visible={popoverVisible}
            onVisibleChange={togglePopover}
            overlayStyle={{ width: '175px' }}
            title={
              <PerFlexRow justify="center" align="middle" className="fullWidth">
                <PerText appearance="heading" fontSize="m" fontWeight="normal" align="center">
                  {t('pyramidsView.forecastModal.buttons.deleteForecast')}
                </PerText>
              </PerFlexRow>
            }
            content={
              <>
                <PerFlexRow mt="xs" mb="s">
                  <PerText color="secondary" fontSize="s">
                    {t('pyramidsView.forecastModal.common.sureDeleteForecast')}
                  </PerText>
                </PerFlexRow>
                <PerFlexRow justify="space-between">
                  <PerButtonCircle
                    icon="close_stroke"
                    color="secondary"
                    variant="pastel"
                    onClick={() => togglePopover(false)}
                    testid="fc-modal-empl-close"
                  />
                  <PerButtonCircle
                    icon="delete_fill"
                    color="error"
                    variant="pastel"
                    onClick={handleDeleteEmployeeForecast}
                    testid="fc-modal-empl-delete"
                  />
                </PerFlexRow>
              </>
            }
          >
            <>
              <PerButton
                text={t('pyramidsView.forecastModal.buttons.deleteEmployeesForecast')}
                color="error"
                before="delete_fill"
                variant="pastel"
                type="button"
                disabled={!employeesSelectedRows.length || !employeeForecastHasChanges}
                onClick={() => togglePopover(true)}
              />
            </>
          </Popover>
        )}
        {activeTab === ForeacastModalTabs.Hirings && (
          <Popover
            trigger="click"
            visible={popoverVisible}
            onVisibleChange={togglePopover}
            overlayStyle={{ width: '175px' }}
            title={
              <PerFlexRow justify="center" align="middle" className="fullWidth">
                <PerText appearance="heading" fontSize="m" fontWeight="normal" align="center">
                  {t('pyramidsView.forecastModal.buttons.deleteForecast')}
                </PerText>
              </PerFlexRow>
            }
            content={
              <>
                <PerFlexRow mt="xs" mb="s">
                  <PerText color="secondary" fontSize="s">
                    {t('pyramidsView.forecastModal.common.sureDeleteForecast')}
                  </PerText>
                </PerFlexRow>
                <PerFlexRow justify="space-between">
                  <PerButtonCircle
                    icon="close_stroke"
                    color="secondary"
                    variant="pastel"
                    onClick={() => togglePopover(false)}
                    testid="fc-modal-nh-close"
                  />
                  <PerButtonCircle
                    icon="delete_fill"
                    color="error"
                    variant="pastel"
                    onClick={handleDeleteHiringsForecast}
                    testid="fc-modal-nh-delete"
                  />
                </PerFlexRow>
              </>
            }
          >
            <>
              <PerButton
                text={t('pyramidsView.forecastModal.buttons.deleteNewHirings')}
                color="error"
                before="delete_fill"
                variant="pastel"
                type="button"
                disabled={!hiringsSelectedRows.length}
                tooltip={!hiringsSelectedRows.length ? t('pyramidsView.forecastModal.buttons.deleteTooltip') : undefined}
                onClick={() => togglePopover(true)}
              />
            </>
          </Popover>
        )}
      </PerFlexRow>
      <PerFlexRow>
        <ForecastModalSaveButton companyId={companyId} country={country} />
      </PerFlexRow>
    </PerFlexRow>
  )
}
